import { Box, Typography, Modal, ModalDialog, ModalClose, Input, FormControl, FormLabel } from "@mui/joy";
import { Trash2, AlertTriangle } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../../../shared";

interface ActionProps {
    onDelete: () => void | Promise<void>;
    isDeleting?: boolean;
    manufacturerName?: string;
}

const DELETE_CONSEQUENCES = [
    "Every product in this manufacturer's catalog",
    "Product aliases and category mappings tied to those products",
    "Supplier inventory lines that reference this manufacturer's products",
];

export function Action({ onDelete, isDeleting = false, manufacturerName }: ActionProps) {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [confirmText, setConfirmText] = useState("");

    const canConfirm = !manufacturerName || confirmText.trim() === manufacturerName.trim();

    function handleDeleteClick() {
        setConfirmText("");
        setShowDeleteDialog(true);
    }

    function handleDeleteConfirm() {
        if (!canConfirm) return;
        setShowDeleteDialog(false);
        onDelete();
    }

    function handleDeleteCancel() {
        setShowDeleteDialog(false);
    }

    return (
        <Box mt={4}>
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "danger.outlinedBorder",
                    borderRadius: "4px",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: "danger.softBg",
                        color: "danger.softColor",
                        borderBottom: "1px solid",
                        borderColor: "danger.outlinedBorder",
                    }}
                >
                    <AlertTriangle size={16} />
                    <Typography level="title-sm" sx={{ color: "inherit" }}>
                        Danger Zone
                    </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                        Delete this manufacturer
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                        Permanently removes{" "}
                        {manufacturerName ? <strong>{manufacturerName}</strong> : "this manufacturer"}{" "}
                        and its product catalog. This cannot be undone.
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={0.75} mt={1.5}>
                        <Typography level="body-xs" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                            Also deleted
                        </Typography>
                        {DELETE_CONSEQUENCES.map((consequence) => (
                            <Box key={consequence} display="flex" alignItems="flex-start" gap={1}>
                                <Box
                                    sx={{
                                        width: 4,
                                        height: 4,
                                        mt: "7px",
                                        flexShrink: 0,
                                        bgcolor: "danger.solidBg",
                                        borderRadius: "1px",
                                    }}
                                />
                                <Typography level="body-sm" color="neutral">
                                    {consequence}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteClick}
                            disabled={isDeleting}
                            startDecorator={<Trash2 size={16} />}
                        >
                            {isDeleting ? "Deleting..." : "Delete Manufacturer"}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Modal open={showDeleteDialog} onClose={handleDeleteCancel}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 460, borderRadius: "4px" }}>
                    <ModalClose />
                    <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ color: "danger.plainColor" }}>
                        <AlertTriangle size={20} />
                        <Typography level="title-md" sx={{ color: "inherit" }}>
                            Delete manufacturer
                        </Typography>
                    </Box>

                    <Typography level="body-sm" color="neutral">
                        This permanently deletes{" "}
                        {manufacturerName ? <strong>{manufacturerName}</strong> : "this manufacturer"} and
                        all associated products. This action cannot be undone.
                    </Typography>

                    {manufacturerName && (
                        <FormControl sx={{ mt: 2 }}>
                            <FormLabel sx={{ fontWeight: 400 }}>
                                Type <strong>{manufacturerName}</strong> to confirm
                            </FormLabel>
                            <Input
                                autoFocus
                                value={confirmText}
                                placeholder={manufacturerName}
                                onChange={(event) => setConfirmText(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && canConfirm) {
                                        event.preventDefault();
                                        handleDeleteConfirm();
                                    }
                                }}
                                sx={{ borderRadius: "4px" }}
                            />
                        </FormControl>
                    )}

                    <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
                        <Button variant="soft" color="neutral" onClick={handleDeleteCancel} disabled={isDeleting}>
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteConfirm}
                            disabled={isDeleting || !canConfirm}
                            loading={isDeleting}
                            startDecorator={<Trash2 size={16} />}
                        >
                            Delete Manufacturer
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
